import React, { CSSProperties, ReactNode, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft,
    ArrowRight,
    ArrowUpRight,
    Check,
    ChevronDown,
    Grid3X3,
    Heart,
    Instagram,
    List,
    Mail,
    Menu,
    MessageCircle,
    Minus,
    Phone,
    Plus,
    Search,
    Shield,
    ShoppingBag,
    Sparkles,
    Truck,
    User,
    X,
    MapPin,
    Trash2,
} from "lucide-react";
import { useMediaQuery } from "@mui/material";
import { useTiendaInfo } from "../../contexts/TiendaInfoContext";
import { HttpCategory } from "../../http";
import { Category } from "../../types/Category";
import Searchbar from "../Header/Navbar/Searchbar";
import { useEditableIdentidad, useTemplateConfig } from "../../templates/templateConfigContext";
import LogoTienda from "../brand/LogoTienda";
import { trackMetric } from "@ecommerce/analytics";

export const Icon = {
    menu: (props: any) => <Menu size={16} strokeWidth={1.6} {...props} />,
    search: (props: any) => <Search size={16} strokeWidth={1.6} {...props} />,
    bag: (props: any) => <ShoppingBag size={16} strokeWidth={1.6} {...props} />,
    user: (props: any) => <User size={16} strokeWidth={1.6} {...props} />,
    heart: (props: any) => <Heart size={16} strokeWidth={1.6} {...props} />,
    arrowLeft: (props: any) => <ArrowLeft size={16} strokeWidth={1.6} {...props} />,
    arrowRight: (props: any) => <ArrowRight size={16} strokeWidth={1.6} {...props} />,
    arrowUpRight: (props: any) => <ArrowUpRight size={16} strokeWidth={1.6} {...props} />,
    plus: (props: any) => <Plus size={16} strokeWidth={1.6} {...props} />,
    minus: (props: any) => <Minus size={16} strokeWidth={1.6} {...props} />,
    close: (props: any) => <X size={16} strokeWidth={1.6} {...props} />,
    truck: (props: any) => <Truck size={16} strokeWidth={1.6} {...props} />,
    shield: (props: any) => <Shield size={16} strokeWidth={1.6} {...props} />,
    spark: (props: any) => <Sparkles size={14} strokeWidth={1.6} {...props} />,
    pin: (props: any) => <MapPin size={16} strokeWidth={1.6} {...props} />,
    check: (props: any) => <Check size={12} strokeWidth={3} {...props} />,
    chevron: (props: any) => <ChevronDown size={12} strokeWidth={2} {...props} />,
    grid: (props: any) => <Grid3X3 size={12} strokeWidth={2} {...props} />,
    list: (props: any) => <List size={12} strokeWidth={2} {...props} />,
    trash: (props: any) => <Trash2 size={14} strokeWidth={1.6} {...props} />,
    instagram: (props: any) => <Instagram size={16} strokeWidth={1.6} {...props} />,
    mail: (props: any) => <Mail size={16} strokeWidth={1.6} {...props} />,
    phone: (props: any) => <Phone size={16} strokeWidth={1.6} {...props} />,
    whatsapp: (props: any) => <MessageCircle size={16} strokeWidth={1.6} {...props} />,
};

export function MarcaTienda({
    size = 22,
    color = "currentColor",
}: {
    size?: number;
    color?: string;
}) {
    return (
        <span
            style={{
                color,
                display: "inline-flex",
                alignItems: "center",
                fontSize: size,
                fontWeight: 600,
                letterSpacing: "0.32em",
                lineHeight: 1,
                textTransform: "uppercase",
            }}
        >
            Mi tienda
        </span>
    );
}

export function PromoStrip() {
    const tiendaInfo = useTiendaInfo();
    const { config } = useTemplateConfig();
    const { bitContentEditMode, emitirSeleccion } = useEditableIdentidad();
    const nombre = tiendaInfo?.nombreTienda ?? config.identidad.vchNombreTienda;
    const descripcion = tiendaInfo?.descripcionTienda ?? "";
    const text =
        config.identidad.vchTextoPromo || (descripcion ? `${nombre} - ${descripcion}` : nombre);

    return (
        <div
            onClick={bitContentEditMode ? emitirSeleccion : undefined}
            style={{
                width: "100%",
                background: "var(--color-primary)",
                color: "var(--btn-dark-text)",
                fontSize: 11.5,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                fontWeight: 600,
                position: "relative",
                zIndex: 30,
                overflow: "hidden",
                height: 36,
                display: "flex",
                alignItems: "center",
                cursor: bitContentEditMode ? "pointer" : undefined,
                outline: bitContentEditMode ? "2px dashed rgba(255,255,255,0.6)" : undefined,
                outlineOffset: -2,
            }}
        >
            <style>{`
        @keyframes tienda-base-marquee {
          from { transform: translateX(0); }
          to { transform: translateX(-10%); }
        }
      `}</style>
            <div
                style={{
                    display: "inline-flex",
                    animation: "tienda-base-marquee 18s linear infinite",
                    whiteSpace: "nowrap",
                }}
            >
                {Array.from({ length: 10 }, (_, i) => (
                    <span key={i} style={{ paddingRight: "5rem" }}>
                        {text}
                    </span>
                ))}
            </div>
        </div>
    );
}

type GlassNavProps = {
    active?: string;
    cartCount?: number;
    onMenuClick: () => void;
    onCartClick: () => void;
    categories?: Category[];
};

export function GlassNav({
    active = "home",
    cartCount = 0,
    onMenuClick,
    onCartClick,
    categories = [],
}: GlassNavProps) {
    const isMobile = useMediaQuery("(max-width:768px)");
    const { config } = useTemplateConfig();

    const sortedCategories = [...categories].sort(
        (a, b) => (a.orden ?? 999999) - (b.orden ?? 999999),
    );

    const navItems: [string, string, string][] = [
        ["Inicio", "home", "/"],
        ...sortedCategories.map((cat): [string, string, string] => [
            cat.nombre,
            cat.sku,
            `/categoria/${cat.sku}`,
        ]),
        [config.identidad.vchLabelCatalogo ?? "Catálogo", "catalogo", "/productos"],
    ];

    const iconBtnStyle: React.CSSProperties = {
        width: 38,
        height: 38,
        borderRadius: 10,
        background: "transparent",
        border: "1px solid var(--divider)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: "var(--ink-900)",
        transition: "background 0.15s ease, border-color 0.15s ease",
        flexShrink: 0,
    };

    return (
        <div
            style={{
                position: "sticky",
                top: 0,
                zIndex: 25,
                width: "100%",
                background: "var(--appbar-bg)",
                backdropFilter: "var(--blur-normal)",
                WebkitBackdropFilter: "var(--blur-normal)",
                borderBottom: "1px solid var(--divider)",
                boxShadow: "0 2px 20px rgba(0,0,0,0.06)",
            }}
        >
            <div
                style={{
                    maxWidth: 1320,
                    margin: "0 auto",
                    padding: isMobile ? "0 16px" : "0 32px",
                    height: isMobile ? 60 : 72,
                    display: "grid",
                    gridTemplateColumns: isMobile ? "auto 1fr auto" : "auto 1fr auto",
                    alignItems: "center",
                    gap: isMobile ? 10 : 24,
                }}
            >
                {/* Izquierda: hamburger + logo */}
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button
                        type="button"
                        onClick={onMenuClick}
                        style={iconBtnStyle}
                        aria-label="Menú"
                    >
                        <Icon.menu />
                    </button>
                    <Link
                        to="/"
                        aria-label={config.identidad.vchLogoAlt}
                        style={{ display: "flex", alignItems: "center", opacity: 1 }}
                    >
                        <LogoTienda height={isMobile ? 26 : 30} />
                    </Link>
                </div>

                {/* Centro: links de navegación (solo desktop, oculto si hay barra de categorías) */}
                {!isMobile && !config.layout.bitBarraCategorias ? (
                    <nav
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                            fontSize: 14,
                            fontWeight: 500,
                        }}
                    >
                        {navItems.map(([label, key, href]) => (
                            <Link
                                key={key}
                                to={href}
                                onClick={() => {
                                    if (href.startsWith("/categoria/")) {
                                        trackMetric("CATEGORY_CLICK", { entitySku: key });
                                    }
                                }}
                                style={{
                                    padding: "7px 14px",
                                    borderRadius: 8,
                                    color: active === key ? "var(--gold)" : "var(--ink-500)",
                                    background:
                                        active === key ? "var(--surface-hover)" : "transparent",
                                    textDecoration: "none",
                                    transition: "color .15s, background .15s",
                                    whiteSpace: "nowrap",
                                    fontWeight: active === key ? 600 : 500,
                                }}
                            >
                                {label}
                            </Link>
                        ))}
                    </nav>
                ) : (
                    <div />
                )}

                {/* Derecha: search (desktop) + carrito */}
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {!isMobile && (
                        <div style={{ width: 188 }}>
                            <Searchbar />
                        </div>
                    )}

                    <button
                        type="button"
                        onClick={onCartClick}
                        style={{ ...iconBtnStyle, position: "relative" }}
                        aria-label="Carrito"
                    >
                        <Icon.bag />
                        {cartCount > 0 && (
                            <span
                                style={{
                                    position: "absolute",
                                    top: -5,
                                    right: -5,
                                    minWidth: 17,
                                    height: 17,
                                    padding: "0 4px",
                                    borderRadius: 999,
                                    background: "var(--color-primary)",
                                    color: "var(--btn-dark-text)",
                                    fontSize: 9.5,
                                    fontWeight: 700,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: "2px solid var(--cream-100)",
                                    lineHeight: 1,
                                }}
                            >
                                {cartCount}
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

type ImgPhProps = {
    label?: string;
    ratio?: string;
    style?: CSSProperties;
    children?: ReactNode;
    src?: string;
    brightness?: number;
};

export function ImgPh({
    label,
    ratio = "1/1",
    style = {},
    children,
    src,
    brightness = 1,
}: ImgPhProps) {
    return (
        <div
            className="img-ph"
            style={{
                aspectRatio: ratio,
                width: "100%",
                borderRadius: "inherit",
                position: "relative",
                overflow: "hidden",
                ...style,
            }}
        >
            {src ? (
                <img
                    src={src}
                    alt={label || "imagen"}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "center",
                        filter: `brightness(${brightness})`,
                    }}
                />
            ) : (
                <span>{label || "imagen"}</span>
            )}
            {children}
        </div>
    );
}

export function SectionHeader({
    eyebrow,
    title,
    action,
}: {
    eyebrow: string;
    title: string;
    action?: ReactNode;
}) {
    return (
        <div
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "end",
                marginBottom: 28,
                padding: "0 8px",
            }}
        >
            <div>
                <div className="eyebrow" style={{ marginBottom: 10 }}>
                    {eyebrow}
                </div>
                <h2 className="h-section" style={{ margin: 0 }}>
                    {title}
                </h2>
            </div>
            {action}
        </div>
    );
}

export function GlassFooter({
    companyName,
    year = new Date().getFullYear(),
}: {
    companyName?: string;
    year?: number;
}) {
    const tiendaInfo = useTiendaInfo();
    const { config } = useTemplateConfig();
    const isMobile = useMediaQuery("(max-width:768px)");
    const storeName = tiendaInfo?.nombreTienda ?? companyName ?? config.identidad.vchNombreTienda;
    const description = tiendaInfo?.descripcionTienda ?? config.identidad.vchDescripcion;
    const instagramUrl = tiendaInfo?.instagram ?? "";
    const instagramHandle = instagramUrl
        ? instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\//, "").replace(/\/$/, "")
        : "";
    const whatsapp = tiendaInfo?.whatsappNumero ?? "";
    const whatsappHabilitado = tiendaInfo?.whatsappHabilitado ?? false;
    const whatsappMessage = config.identidad.vchWhatsappMensaje;
    const phone = tiendaInfo?.telefonoLabel ?? "";
    const phoneHref = tiendaInfo?.telefonoHref ?? "";
    const email = tiendaInfo?.email ?? "";
    const addressLine1 = tiendaInfo?.direccionLinea1 ?? "";
    const addressLine2 = tiendaInfo?.direccionLinea2 ?? "";
    const copyright =
        tiendaInfo?.textoCopyright ?? `© Copyright ${storeName} - ${year} | Powered by ItapTec`;

    const [categories, setCategories] = useState<Category[]>([]);
    useEffect(() => {
        HttpCategory.getAllCategories()
            .then((data: Category[]) =>
                setCategories(
                    data
                        .filter((c) => c.idCategoriaPadre === null)
                        .sort((a, b) => (a.orden ?? 999999) - (b.orden ?? 999999)),
                ),
            )
            .catch(() => {});
    }, []);

    const hasContact =
        instagramUrl || (whatsappHabilitado && whatsapp) || phone || email || addressLine1;
    const footerMaxWidth = 1120;
    const logoWidth = isMobile ? 132 : 156;

    const colLabel: CSSProperties = {
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
        color: "var(--gold)",
        marginBottom: 18,
    };

    const navLink: CSSProperties = {
        fontSize: 14,
        fontWeight: 500,
        color: "var(--ink-700)",
        textDecoration: "none",
        display: "block",
        lineHeight: 1.35,
    };

    const contactItem: CSSProperties = {
        fontSize: 14,
        fontWeight: 500,
        color: "var(--ink-700)",
        textDecoration: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        lineHeight: 1.35,
    };

    return (
        <footer
            style={{
                padding: isMobile ? "56px 12px 28px" : "80px 32px 32px",
                position: "relative",
                zIndex: 2,
            }}
        >
            <div
                className="lg-strong"
                style={{
                    width: "100%",
                    boxSizing: "border-box",
                    padding: isMobile ? "36px 22px 32px" : "50px clamp(42px, 5vw, 76px) 44px",
                    borderRadius: isMobile ? 28 : 32,
                }}
            >
                <div
                    style={{
                        display: "grid",
                        maxWidth: footerMaxWidth,
                        margin: "0 auto",
                        gridTemplateColumns: isMobile
                            ? "1fr"
                            : hasContact
                              ? "repeat(3, minmax(0, 1fr))"
                              : "repeat(2, minmax(0, 1fr))",
                        gap: isMobile ? 36 : 48,
                        alignItems: "start",
                    }}
                >
                    {/* Marca */}
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 18,
                            alignItems: isMobile ? "center" : "flex-start",
                            textAlign: isMobile ? "center" : "left",
                            justifySelf: isMobile ? "center" : "start",
                        }}
                    >
                        <LogoTienda
                            variante="stacked"
                            width={logoWidth}
                            height={isMobile ? 86 : 104}
                            align={isMobile ? "center" : "left"}
                        />
                        {description && (
                            <p
                                style={{
                                    margin: 0,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: "var(--ink-500)",
                                    maxWidth: isMobile ? 280 : 250,
                                    lineHeight: 1.65,
                                }}
                            >
                                {description}
                            </p>
                        )}
                    </div>

                    {/* Tienda */}
                    <div
                        style={{
                            textAlign: "center",
                            justifySelf: isMobile ? "center" : hasContact ? "center" : "end",
                        }}
                    >
                        <div style={colLabel}>Tienda</div>
                        <nav
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 12,
                                alignItems: "center",
                            }}
                        >
                            <Link to="/" style={navLink}>
                                Inicio
                            </Link>
                            {categories.map((cat) => (
                                <Link
                                    key={cat.id}
                                    to={`/categoria/${cat.sku}`}
                                    onClick={() =>
                                        trackMetric("CATEGORY_CLICK", { entitySku: cat.sku })
                                    }
                                    style={navLink}
                                >
                                    {cat.nombre}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Contacto */}
                    {hasContact && (
                        <div
                            style={{
                                textAlign: isMobile ? "center" : "left",
                                justifySelf: isMobile ? "center" : "end",
                            }}
                        >
                            <div style={colLabel}>Contacto</div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 12,
                                    alignItems: isMobile ? "center" : "flex-start",
                                }}
                            >
                                {instagramUrl && (
                                    <a
                                        href={instagramUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={contactItem}
                                    >
                                        <Icon.instagram /> @{instagramHandle}
                                    </a>
                                )}
                                {whatsappHabilitado && whatsapp && (
                                    <a
                                        href={`https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(whatsappMessage)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={contactItem}
                                    >
                                        <Icon.whatsapp /> {whatsapp}
                                    </a>
                                )}
                                {phone &&
                                    (phoneHref ? (
                                        <a href={phoneHref} style={contactItem}>
                                            <Icon.phone /> {phone}
                                        </a>
                                    ) : (
                                        <span style={contactItem}>
                                            <Icon.phone /> {phone}
                                        </span>
                                    ))}
                                {email && (
                                    <a href={`mailto:${email}`} style={contactItem}>
                                        <Icon.mail /> {email}
                                    </a>
                                )}
                                {(addressLine1 || addressLine2) && (
                                    <span style={{ ...contactItem, alignItems: "flex-start" }}>
                                        <Icon.pin style={{ marginTop: 2, flexShrink: 0 }} />
                                        <span>
                                            {addressLine1}
                                            {addressLine1 && addressLine2 && <br />}
                                            {addressLine2}
                                        </span>
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div
                style={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "space-between",
                    alignItems: "center",
                    textAlign: isMobile ? "center" : "left",
                    color: "var(--ink-400)",
                    fontSize: 12,
                    fontWeight: 500,
                    width: "100%",
                    boxSizing: "border-box",
                    padding: isMobile ? "18px 8px 0" : "18px 8px 0",
                    flexWrap: "wrap",
                    gap: 10,
                }}
            >
                <span>{copyright}</span>
                <span>Todos los derechos reservados.</span>
            </div>
        </footer>
    );
}
